import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, List, ListItemButton,
  ListItemText, CircularProgress, Alert, Button, Box, IconButton,
  Avatar, ListItemAvatar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAddress } from '../utils/addressUtils';
import { apiFetch } from '../apiFetch';
import { Store } from '../types/Store';
import StoreFormModal from '../modals/StoreFormModal';
import DeleteModal from '../modals/DeleteModal';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTutorial } from '../hooks/useTutorial';

const Stores: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingStore, setDeletingStore] = useState<Store | null>(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  useTutorial('stores', { hasData: stores.length > 0 });

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shops/`);
        setStores(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching shops.');
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, [apiUrl]);

  const filteredStores = stores
  .filter((store) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      (store.name && store.name.toLowerCase().includes(lowerSearch)) ||
      (store.address_line1 && store.address_line1.toLowerCase().includes(lowerSearch)) ||
      (store.address_line2 && store.address_line2.toLowerCase().includes(lowerSearch)) ||
      (store.city && store.city.toLowerCase().includes(lowerSearch)) ||
      (store.state && store.state.toLowerCase().includes(lowerSearch)) ||
      (store.postal_code && store.postal_code.toLowerCase().includes(lowerSearch)) ||
      (store.country && store.country.toLowerCase().includes(lowerSearch))
    );
  })
  .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Handle opening the Add modal
  const handleAddClick = () => {
    setEditingStore(null);
    setFormModalOpen(true);
  };

  // Handle opening the Edit modal
  const handleEditClick = (e: React.MouseEvent, store: Store) => {
    e.preventDefault(); // Prevent navigation to detail page
    e.stopPropagation();
    setEditingStore(store);
    setFormModalOpen(true);
  };

  // Handle opening the Delete modal
  const handleDeleteClick = (e: React.MouseEvent, store: Store) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingStore(store);
    setDeleteModalOpen(true);
  };

  // Handle successful form submission (add or edit)
  const handleFormSuccess = (savedStore: Store) => {
    if (editingStore) {
      // Update existing store in the list
      setStores((prev) =>
        prev.map((s) => (s.id === savedStore.id ? savedStore : s))
      );
      setSuccessMessage('Store updated successfully.');
    } else {
      // Add new store to the list
      setStores((prev) => [...prev, savedStore]);
      setSuccessMessage('Store added successfully.');
    }
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingStore) return;
    try {
      await apiFetch(`${apiUrl}/api/shops/${deletingStore.id}/`, {
        method: 'DELETE',
      });
      setStores((prev) => prev.filter((s) => s.id !== deletingStore.id));
      setSuccessMessage('Store deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the store.');
    } finally {
      setDeleteModalOpen(false);
      setDeletingStore(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Manage Your Stores</Typography>
        </Box>
        <Box className="page-header-buttons">
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddClick} data-tutorial="stores-add-button">
            Add Store
          </Button>
        </Box>
      </Box>
      <Typography variant="body1" gutterBottom>
        Here you can set up and manage the stores and supermarkets you shop at. Add new stores, update their details, and organize your shopping preferences.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search bar to filter stores */}
      <TextField
        label="Search Stores"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        data-tutorial="stores-search"
      />

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <List data-tutorial="stores-list">
        {filteredStores.map((store) => (
          <ListItemButton
            key={store.id}
            component={RouterLink}
            to={`/stores/${store.id}`}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <ListItemAvatar>
              <Avatar
                src={store.icon_url || store.image_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={store.name}
              secondary={formatAddress(store)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => handleEditClick(e, store)}
                aria-label="edit store"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, store)}
                aria-label="delete store"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </ListItemButton>
        ))}
      </List>

      {!loading && stores.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No stores yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the "Add Store" button above to add your first store.
          </Typography>
        </Box>
      )}

      {/* Add/Edit Store Modal */}
      <StoreFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        store={editingStore}
        existingStores={stores}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingStore(null);
        }}
        onDelete={handleDeleteConfirm}
        itemName={deletingStore?.name || ''}
      />
    </Container>
  );
};

export default Stores;